// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Box, Button, Center, Group, Loader, Paper, SegmentedControl, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { WithId } from '@medplum/core';
import { createReference, getReferenceString, normalizeErrorString } from '@medplum/core';
import type { Practitioner, Schedule } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { ProviderRoleCode } from '@polarissleep/scheduling';
import { IconCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { FormCard } from '../../../components/FormCard';
import { useAppointmentTypes } from '../../../hooks/useAppointmentTypes';
import { useRole } from '../../../hooks/useRole';
import { getExtensionValue, TIMEZONE_EXTENSION } from '../../../utils/practitioner';
import { withTenantScope } from '../../../utils/tenant';
import type { AvailabilityBlock } from './availability/types';
import { useAvailabilityEditorState } from './availability/useAvailabilityEditorState';
import { WeeklyAvailabilityGrid } from './availability/WeeklyAvailabilityGrid';
import { WeekNavigator } from './availability/WeekNavigator';

export type AvailabilityMode = 'specific' | 'general';

interface AvailabilitySectionProps {
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
}

export function AvailabilitySection({ isEditing, onStartEdit, onEndEdit }: AvailabilitySectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const { role, isAdmin, loading: roleLoading } = useRole();
  const { appointmentTypes, loading: typesLoading } = useAppointmentTypes();

  const [mode, setMode] = useState<AvailabilityMode>('specific');
  const [weekStart, setWeekStart] = useState<Date>(() => dayjs().startOf('week').toDate());
  const [schedule, setSchedule] = useState<WithId<Schedule> | undefined>();
  const [draftBlocks, setDraftBlocks] = useState<AvailabilityBlock[] | null>(null);

  useEffect(() => {
    if (medplum.isLoading() || !profile) {
      return;
    }
    const loadSchedule = async (): Promise<void> => {
      const found = (await medplum.searchOne('Schedule', {
        actor: getReferenceString(profile),
        active: 'true',
      })) as WithId<Schedule> | undefined;
      if (found) {
        setSchedule(found);
        return;
      }
      const created = await medplum.createResource<Schedule>(
        withTenantScope({
          resourceType: 'Schedule',
          actor: [createReference(profile)],
          active: true,
        })
      );
      setSchedule(created);
    };
    loadSchedule().catch(console.error);
  }, [medplum, profile]);

  const { blocks, loading, saving, save } = useAvailabilityEditorState({
    schedule,
    mode,
    weekStart,
    providerRoleCode: role === 'clinical-ops-rcm' ? undefined : role,
    isAdmin,
    appointmentTypes,
    timezone: getExtensionValue(profile, TIMEZONE_EXTENSION),
  });

  const visibleBlocks = useMemo(() => draftBlocks ?? blocks, [draftBlocks, blocks]);

  const handleStartEdit = (): void => {
    setDraftBlocks(blocks);
    onStartEdit();
  };

  const handleCancel = (): void => {
    setDraftBlocks(null);
    onEndEdit();
  };

  const handleSave = async (): Promise<void> => {
    if (!draftBlocks) {
      onEndEdit();
      return;
    }
    try {
      await save(draftBlocks);
      setDraftBlocks(null);
      onEndEdit();
      showNotification({
        color: 'green',
        icon: <IconCheck size={16} />,
        message: 'Availability saved',
      });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    }
  };

  const handlePrevWeek = (): void => {
    if (isEditing) {
      return;
    }
    setWeekStart((d) => dayjs(d).subtract(1, 'week').toDate());
  };

  const handleNextWeek = (): void => {
    if (isEditing) {
      return;
    }
    setWeekStart((d) => dayjs(d).add(1, 'week').toDate());
  };

  const action = (
    <Group gap="xs">
      <SegmentedControl
        size="xs"
        value={mode}
        disabled={isEditing}
        onChange={(v) => setMode(v as AvailabilityMode)}
        data={[
          { label: 'Specific Week', value: 'specific' },
          { label: 'General', value: 'general' },
        ]}
      />
      {isEditing ? (
        <>
          <Button size="xs" variant="default" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button size="xs" onClick={() => handleSave()} loading={saving}>
            Save
          </Button>
        </>
      ) : (
        <Button size="xs" onClick={handleStartEdit} disabled={!schedule || roleLoading || typesLoading}>
          Edit
        </Button>
      )}
    </Group>
  );

  const isLoading = loading || roleLoading || typesLoading || !schedule;

  return (
    <FormCard title="Availability" isEditing={isEditing} action={action}>
      {mode === 'specific' ? (
        <Box mb="md">
          <WeekNavigator weekStart={weekStart} onPrev={handlePrevWeek} onNext={handleNextWeek} />
        </Box>
      ) : (
        <Paper bg="gray.0" radius="md" p="sm" mb="md">
          <Title order={4} fw={700} ta="center">
            General Availability
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Recurring weekly availability used for future weeks without an override.
          </Text>
        </Paper>
      )}
      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <WeeklyAvailabilityGrid
            mode={mode}
            weekStart={weekStart}
            blocks={visibleBlocks}
            isEditing={isEditing}
            onChange={setDraftBlocks}
          />
          {isEditing && (
            <Text size="xs" c="dimmed" mt="xs">
              Drag on an empty day to draw availability. Drag the top or bottom edge of a block to resize, or drag its
              body to move it. Hover a block and click the × to delete.
            </Text>
          )}
        </>
      )}
    </FormCard>
  );
}
